import { useEffect, useMemo, useState } from 'react';
import {
  actionType,
  initialAddressState,
  initialEmployeeState,
  serviceSlotType,
  toastMessages,
} from 'config/constants';
import {
  Box,
  Card,
  CardMedia,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import { getAvailableEmployeeLevelsBySelectedLevel } from 'utils';
import { isNil } from 'lodash';
import { useAddresses, useAddressesFunctions } from 'contexts/AddressContext';
import { useEmployee, useEmployeeFunctions } from 'contexts/EmployeeContext';
import { useFormik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import * as Yup from 'yup';
import ActionOnActivityModal from 'components/ActionOnActivityModal';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import AddressForm from '../AddressForm';
import CheckIcon from '@material-ui/icons/Check';
import clsx from 'clsx';
import DefaultButton from 'components/DefaultButton';
import DefaultModal from '../DefaultModal';
import DefaultSwitch from 'components/DefaultSwitch';
import DefaultMenuItem from 'components/DefaultMenuItem';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import EmployeeForm from '../EmployeeForm';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SwitchStatus from 'components/SwitchStatus';
import toast from 'react-hot-toast';
import uploadLogo from '_assets/parameters/upload.svg';
import useShowActionModal from 'hooks/useShowActionModal';
import useStyles from './styles';

const validationSchema = Yup.object().shape(
  {
    courseType: Yup.string().required('Champ obligatoire'),
    description: Yup.string().required('Champ obligatoire'),
    duration: Yup.number()
      .positive('La durée ne peut être nulle')
      .min(60, 'Durée inférieur à 60 minutes')
      .required('Champ obligatoire'),
    identifier: Yup.string().required('Champ obligatoire'),
    level: Yup.string().required('Champ obligatoire'),
    maxAge: Yup.number()
      .integer()
      .when('minAge', (minAge, schema) => {
        return schema.test({
          test: (maxAge) => (!minAge ? true : maxAge >= minAge),
          message: "Age inférieur à l'âge minimum",
        });
      })
      .test({
        test: (age) => age > 0,
        message: "L'âge maximum doit être supérieur à 0",
      })
      .required('Champ obligatoire'),
    minAge: Yup.number()
      .integer()
      .when('maxAge', (maxAge, schema) => {
        return schema.test({
          test: (minAge) => (!maxAge ? true : minAge <= maxAge),
          message: "Age supérieur à l'âge maximum",
        });
      })
      .test({
        test: (age) => age > 0,
        message: "L'âge minimum doit être supérieur à 0",
      })
      .required('Champ obligatoire'),
    priceHighSeason: Yup.number()
      .when('priceLowSeason', (priceLowSeason, schema) => {
        return schema.test({
          test: (priceHighSeason) => (!priceLowSeason ? true : priceHighSeason >= priceLowSeason),
          message: 'Prix inférieur au prix basse saison',
        });
      })
      .test({
        test: (price) => price > 0,
        message: 'Le prix doit être supérieur à 0',
      })
      .required('Champ obligatoire'),
    priceLowSeason: Yup.number()
      .when('priceHighSeason', (priceHighSeason, schema) => {
        return schema.test({
          test: (priceLowSeason) => (!priceHighSeason ? true : priceLowSeason <= priceHighSeason),
          message: 'Prix supérieur au prix haute saison',
        });
      })
      .test({
        test: (price) => price > 0,
        message: 'Le prix doit être supérieur à 0',
      })
      .required('Champ obligatoire'),
    productName: Yup.string().required('Champ obligatoire'),
    publish: Yup.boolean(),
    slots: Yup.number().min(1, 'Nombre de places inférieur à 1').required('Champ obligatoire'),
  },
  [
    ['minAge', 'maxAge'],
    ['priceLowSeason', 'priceHighSeason'],
  ],
);

const StyledTableCell = withStyles((theme) => ({
  root: {
    border: 'none',
    color: theme.palette.primary.main,
  },
  head: {
    fontSize: '1rem',
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.blue.lighter,
    },
  },
}))(TableRow);

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: theme.palette.text.primary,
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function ActivityForm({
  activity,
  dialog,
  disabled,
  message = '',
  onCancel = () => {},
  onStatusChange = () => {},
  onSubmit = () => {},
  socialToggle = false,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const { addresses: globalAddresses } = useAddresses();
  const { createAddress } = useAddressesFunctions();
  const { employees: globalEmployees } = useEmployee();
  const { createEmployee } = useEmployeeFunctions();

  const addresses = useMemo(() => globalAddresses.filter((a) => a.enabled), [globalAddresses]);

  const [employees, setEmployees] = useState(globalEmployees);
  const [picture, setPicture] = useState({ file: null, url: activity?.ImgPath ?? '' });

  const [addressToAdd, setAddressToAdd] = useState(addresses[0] ?? null);
  const [employeeToAdd, setEmployeeToAdd] = useState(employees[0] ?? null);
  const [employeesAndAddresses, setEmployeesAndAddresses] = useState(activity?.employeesAndAddresses ?? []);
  const [status, setStatus] = useState(activity?.status ?? 'enabled');
  const [updateState, setUpdateState] = useState({ address: null, employee: null });
  const [showError, setShowError] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [createModalsOpen, setCreateModalsOpen] = useState({ address: false, employee: false });

  const disableAdd = employeesAndAddresses.some(
    ({ address, employee }) => address.id === addressToAdd.id && employee?.id === employeeToAdd?.id,
  );

  const { errors, handleChange, handleSubmit, isSubmitting, resetForm, setFieldValue, setValues, touched, values } =
    useFormik({
      initialValues: { ...activity },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        if (employeesAndAddresses.length === 0) {
          setShowError(true);
          return;
        }

        let newValues = { ...values };
        delete newValues.status;
        let data = { ...newValues };
        if (isNil(activity?.id)) data = { ...data, status };
        const { pictureSizeError } = await onSubmit({ ...data, employeesAndAddresses }, { ...picture });
        if (!pictureSizeError) setPicture({ file: null, url: activity?.ImgPath ?? '' });
      },
    });

  const { resetShowAction, setActionConflictSelect, shouldShowAction, showAction } = useShowActionModal(
    actionType.DEACTIVATE,
    'activity',
    deactivateActivity,
  );

  useEffect(() => {
    resetForm();
    setPicture({ file: null, url: activity?.ImgPath ?? '' });
    setStatus(activity.status);
    setValues(activity);
  }, [activity, resetForm, setValues]);

  useEffect(() => {
    const availableLevels = getAvailableEmployeeLevelsBySelectedLevel(values.level);
    const monitorList = globalEmployees.filter(
      (e) =>
        e.is_enabled &&
        availableLevels.includes(e.level) &&
        !employeesAndAddresses.some(({ employee }) => employee.id === e.id),
    );
    setEmployees(monitorList);
    setEmployeeToAdd(monitorList[0] ?? null);
  }, [employeesAndAddresses, globalEmployees, values.level]);

  function deactivateActivity() {
    onStatusChange({ enabled: false });
  }

  function handleCancelDeactivation() {
    setStatus('enabled');
    resetShowAction();
  }

  const handleDeactivateAction = async (force = false) => {
    setActionLoading(true);
    const response = await onStatusChange({ enabled: false, force });
    setActionLoading(false);
    return response;
  };

  const handleConfirmActionOnAddressModal = async ({ forceAction }) => {
    // Handle confirmation on deactivation
    if (!isNil(forceAction)) {
      if (forceAction) {
        const { success } = await handleDeactivateAction(true);
        if (success) setStatus('disabled');
      }
      return resetShowAction();
    }

    // Handle preference passed
    const { conflict, success } = await handleDeactivateAction();
    if (conflict) return setActionConflictSelect();
    if (success) setStatus('disabled');
    resetShowAction();
  };

  const handleStatusChanged = (checked) => {
    if (checked) {
      let currentStatus = status;
      if (!isNil(activity.id) && activity.employeesAndAddresses.length > 0) {
        setStatus('enabled');
        return onStatusChange({ enabled: true });
      }
      toast.error(toastMessages.error.ACTIVITY_CANNOT_ENABLE);
      return setStatus(currentStatus);
    }

    if (isNil(activity.id)) {
      return setStatus('disabled');
    }

    shouldShowAction();
  };

  const onPictureChange = async (event) => {
    if (event.target.files != null && event.target.files.length > 0) {
      const file = new Blob([event.currentTarget.files[0]], { type: 'image/*' });
      const fileURL = URL.createObjectURL(file);
      setPicture({ file: event.target.files[0], url: fileURL });
    }
  };

  const handleCourseTypeChange = ({ target: { value } }) => {
    setFieldValue('courseType', value);
    if (value !== 'public') setFieldValue('slots', 1);
    else setFieldValue('slots', 2);
  };

  const handleAddEmployeeAndAddress = () => {
    if (!employeeToAdd || !addressToAdd) return;

    setEmployeesAndAddresses((prev) => [...prev, { address: addressToAdd, employee: employeeToAdd }]);
    if (showError) setShowError(false);
  };

  const onConfirmAddressUpdateClick = () => {
    const index = employeesAndAddresses.findIndex((ea) => ea.employee.id === updateState.employee.id);
    let newItems = [...employeesAndAddresses];
    newItems.splice(index, 1, { address: updateState.address, employee: employeesAndAddresses[index].employee });
    setEmployeesAndAddresses(newItems);
    setUpdateState({ address: null, employee: null });
  };

  const onDeleteEmployeeAndAddressClick = (employee) => {
    setEmployeesAndAddresses((prev) => prev.filter((ea) => ea.employee.id !== employee.id));
    setUpdateState({ address: null, employee: null });
  };

  const onCreateEmployeeClick = () => {
    setCreateModalsOpen((prev) => ({ ...prev, employee: true }));
  };

  const onCreateAddressClick = () => {
    setCreateModalsOpen((prev) => ({ ...prev, address: true }));
  };

  const handleCreateEmployee = async ({ values, picture }) => {
    let pictureData = null;
    if (picture?.file) {
      let formData = new FormData();
      formData.append('file', picture?.file);
      pictureData = { data: formData, url: picture?.url };
    }

    const { created, pictureUpdated } = await createEmployee(values, pictureData);
    if (created) setCreateModalsOpen((prev) => ({ ...prev, employee: false }));

    return { pictureUpdated, success: created };
  };

  const handleCreateAddress = async (values) => {
    const body = {
      address: values.address_name,
      city: values.city_name,
      title: values.title,
      index: values.street_index,
      number: values.street_number,
      zipCode: values.zip_code,
    };

    const { created } = await createAddress(body);
    if (created) setCreateModalsOpen((prev) => ({ ...prev, address: false }));

    return { success: created };
  };

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    onCancel();
    resetForm();
    setPicture({ file: null, url: activity?.ImgPath ?? '' });
  };

  const handlePictureClick = (event) => {
    event.target.value = '';
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit} style={{ height: '100%', width: '100%' }}>
      <Grid container justifyContent="center" style={{ height: '100%' }}>
        <Grid
          container
          item
          xs={12}
          xl={10}
          alignItems="center"
          justifyContent="center"
          direction="column"
          spacing={3}
          style={{ flexGrow: 1 }}
        >
          <Grid container justifyContent="center">
            <Divider className={classes.divider} />

            <Box className={classes.statusBox}>
              <Typography>Statut</Typography>
              <DefaultSwitch
                checked={status === 'enabled'}
                checkedIcon={CheckIcon}
                color={theme.palette.primary.main}
                onChange={(_, checked) => handleStatusChanged(checked)}
              />
              <SwitchStatus status={status === 'enabled'} />
              <Typography className={classes.statusDescription}>Mettre en ligne la prestation</Typography>
            </Box>

            <Divider className={classes.divider} />
          </Grid>

          <Grid container>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Nom de l'activité"
                  name="productName"
                  variant="outlined"
                  value={values.productName}
                  onChange={handleChange}
                  error={touched.productName && Boolean(errors.productName)}
                  helperText={touched.productName && errors.productName}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="ID Produit"
                  name="identifier"
                  variant="outlined"
                  value={values.identifier}
                  onChange={handleChange}
                  error={touched.identifier && Boolean(errors.identifier)}
                  helperText={touched.identifier && errors.identifier}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container>
            <Grid container spacing={3}>
              <Grid item xs={4} lg={!!dialog ? 4 : 3}>
                <Card className={classes.imageContainer}>
                  <CardMedia className={classes.cardMedia} image={picture?.url ?? null} />

                  <input
                    accept="image/*"
                    className={classes.input}
                    onChange={onPictureChange}
                    onClick={handlePictureClick}
                    id="serviceImageInput"
                    name="serviceImage"
                    type="file"
                  />

                  <label className={classes.inputField} htmlFor="serviceImageInput">
                    <IconButton aria-label="upload picture" color="primary" component="span" size="small">
                      <img src={uploadLogo} alt="Upload logo" width="75%" style={{ marginTop: '5%' }} />
                    </IconButton>
                    <Box className={classes.textFormat}>{picture.url ? '' : 'Ajouter une photo'}</Box>
                  </label>
                </Card>
              </Grid>

              <Grid item xs={8} lg={!!dialog ? 8 : 9}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={6}
                  label="Description"
                  name="description"
                  variant="outlined"
                  value={values.description}
                  onChange={handleChange}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Prix haute saison TTC"
                  name="priceHighSeason"
                  variant="outlined"
                  type="number"
                  value={values.priceHighSeason}
                  onChange={handleChange}
                  error={touched.priceHighSeason && Boolean(errors.priceHighSeason)}
                  helperText={touched.priceHighSeason && errors.priceHighSeason}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Prix basse saison TTC"
                  name="priceLowSeason"
                  variant="outlined"
                  type="number"
                  value={values.priceLowSeason}
                  onChange={handleChange}
                  error={touched.priceLowSeason && Boolean(errors.priceLowSeason)}
                  helperText={touched.priceLowSeason && errors.priceLowSeason}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  select
                  label="Niveau"
                  name="level"
                  variant="outlined"
                  value={values.level}
                  onChange={handleChange}
                  error={touched.level && Boolean(errors.level)}
                  helperText={touched.level && errors.level}
                >
                  <DefaultMenuItem value="beginner">Débutant</DefaultMenuItem>
                  <DefaultMenuItem value="intermediate">Intermédiaire</DefaultMenuItem>
                  <DefaultMenuItem value="expert">Expert</DefaultMenuItem>
                  <DefaultMenuItem value="all">Tout niveau</DefaultMenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid container>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Age maximum"
                  name="maxAge"
                  variant="outlined"
                  type="number"
                  value={values.maxAge}
                  onChange={handleChange}
                  error={touched.maxAge && Boolean(errors.maxAge)}
                  helperText={touched.maxAge && errors.maxAge}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Age minimum"
                  name="minAge"
                  variant="outlined"
                  type="number"
                  value={values.minAge}
                  onChange={handleChange}
                  error={touched.minAge && Boolean(errors.minAge)}
                  helperText={touched.minAge && errors.minAge}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              <Grid container item xs={6}>
                <Grid item xs={10}>
                  <TextField
                    disabled={disabled}
                    fullWidth
                    margin="dense"
                    select
                    label="Type de cours"
                    name="courseType"
                    variant="outlined"
                    value={values.courseType}
                    onChange={handleCourseTypeChange}
                    error={touched.courseType && Boolean(errors.courseType)}
                    helperText={touched.courseType && errors.courseType}
                  >
                    {serviceSlotType.map((st) => (
                      <DefaultMenuItem key={`st-${st.value}`} value={st.value}>
                        {st.label}
                      </DefaultMenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid container item xs={2} alignItems="center" justifyContent="center">
                  <StyledTooltip
                    title={
                      <>
                        <Typography color="inherit">Collectif :</Typography>
                        <Typography color="inherit" variant="body2">
                          Activité réservée par plusieurs personnes
                        </Typography>

                        <br />

                        <Typography color="inherit">Groupe :</Typography>
                        <Typography color="inherit" variant="body2">
                          Activité réservée par une personne au nom d'un groupe
                        </Typography>

                        <br />

                        <Typography color="inherit">Individuel :</Typography>
                        <Typography color="inherit" variant="body2">
                          Activité réservée par une seule personne
                        </Typography>
                      </>
                    }
                  >
                    <IconButton>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </StyledTooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  margin="dense"
                  label="Durée (min)"
                  name="duration"
                  variant="outlined"
                  type="number"
                  value={values.duration}
                  onChange={handleChange}
                  error={touched.duration && Boolean(errors.duration)}
                  helperText={touched.duration && errors.duration}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  disabled={disabled || ['private', 'group'].includes(values.courseType)}
                  fullWidth
                  margin="dense"
                  label="Nombre de places"
                  name="slots"
                  variant="outlined"
                  type="number"
                  value={['private', 'group'].includes(values.courseType) ? 1 : values.slots}
                  onChange={handleChange}
                  error={touched.slots && Boolean(errors.slots)}
                  helperText={touched.slots && errors.slots}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {socialToggle && (
                <Grid container item xs={6} alignItems="center">
                  <FormControlLabel
                    className={classes.socialControlLabel}
                    control={<DefaultSwitch checkedIcon={CheckIcon} color={theme.palette.primary.main} />}
                    label="Publier sur les réseaux sociaux"
                    labelPlacement="start"
                    name="publish"
                    value={values.publish}
                    onChange={handleChange}
                    style={{ margin: '0 -8px 0 0' }}
                  />
                  <SwitchStatus status={values.publish} />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Divider className={clsx(classes.divider, classes.spaced)} />

          <Grid container>
            <Box className={classes.monitorPlaceTitleContainer}>
              <Typography variant="h4">Associez des moniteurs avec des lieux</Typography>
            </Box>

            <TableContainer style={{ marginBottom: '20px' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <StyledTableCell style={{ width: '35%' }}>
                      <Select
                        disabled={disabled}
                        displayEmpty
                        fullWidth
                        margin="dense"
                        value={employeeToAdd ?? ''}
                        onChange={(event) => {
                          setEmployeeToAdd(event.target.value);
                        }}
                        renderValue={(employee) => {
                          if (!employee) return 'Choisir un moniteur';
                          return employee.first_name + ' ' + employee.last_name;
                        }}
                      >
                        {employees.length === 0 && (
                          <DefaultMenuItem value="" disabled>
                            Aucun moniteur disponible
                          </DefaultMenuItem>
                        )}
                        {employees.map((employee, index) => (
                          <DefaultMenuItem key={index} value={employee}>
                            {employee.first_name + ' ' + employee.last_name}
                          </DefaultMenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>

                    <StyledTableCell style={{ width: '35%' }}>
                      <Select
                        disabled={disabled}
                        displayEmpty
                        fullWidth
                        margin="dense"
                        value={addressToAdd}
                        onChange={(event) => {
                          setAddressToAdd(event.target.value);
                        }}
                        renderValue={(address) => {
                          if (!address) return 'Choisir un lieu';
                          return address.title;
                        }}
                      >
                        {addresses.length === 0 && (
                          <DefaultMenuItem value="" disabled>
                            Aucun lieu disponible
                          </DefaultMenuItem>
                        )}
                        {addresses.map((address, index) => (
                          <DefaultMenuItem key={index} value={address}>
                            {address.title}
                          </DefaultMenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>

                    <StyledTableCell style={{ textAlign: 'center' }}>
                      <DefaultButton disabled={disableAdd} standard onClick={handleAddEmployeeAndAddress}>
                        Ajouter
                      </DefaultButton>
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '35%' }}>Moniteur</StyledTableCell>
                    <StyledTableCell>Lieu de prestation</StyledTableCell>
                    <StyledTableCell style={{ textAlign: 'center' }}>Éditer</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {employeesAndAddresses.map(({ address, employee }) => (
                    <StyledTableRow>
                      <StyledTableCell style={{ width: '35%' }}>
                        {employee.first_name + ' ' + employee.last_name}
                      </StyledTableCell>
                      {updateState.employee?.id === employee.id ? (
                        <StyledTableCell>
                          <TextField
                            fullWidth
                            margin="dense"
                            select
                            value={updateState.address}
                            onChange={(event) => setUpdateState((prev) => ({ ...prev, address: event.target.value }))}
                          >
                            {addresses.map((address, index) => (
                              <DefaultMenuItem key={index} value={address}>
                                {address.title}
                              </DefaultMenuItem>
                            ))}
                          </TextField>
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell>{address.title}</StyledTableCell>
                      )}

                      <StyledTableCell>
                        <Box style={{ minWidth: '100px', textAlign: 'center' }}>
                          {updateState.employee?.id === employee.id ? (
                            <IconButton
                              aria-label="Confirm"
                              size="small"
                              style={{ marginRight: '5px' }}
                              onClick={onConfirmAddressUpdateClick}
                            >
                              <CheckIcon color="secondary" />
                            </IconButton>
                          ) : (
                            <IconButton
                              aria-label="Editer"
                              disabled={disabled}
                              size="small"
                              style={{ marginRight: '5px' }}
                              onClick={() => setUpdateState({ address, employee })}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                          )}

                          <IconButton
                            aria-label="delete"
                            disabled={disabled}
                            size="small"
                            onClick={() => onDeleteEmployeeAndAddressClick(employee)}
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {showError && (
              <Box style={{ textAlign: 'center', width: '100%' }}>
                <Typography color="error">Aucun moniteur / lieu n'a été assigné</Typography>
              </Box>
            )}

            <Grid className={classes.bottomButtons} container>
              <Grid container item xs={6} alignItems="center" direction="column" justifyContent="center">
                <Divider style={{ width: '60%' }} />

                <DefaultButton
                  className={classes.button}
                  startIcon={<AddCircleOutlineRoundedIcon />}
                  onClick={onCreateEmployeeClick}
                >
                  Créer un moniteur
                </DefaultButton>
              </Grid>

              <Grid container item xs={6} alignItems="center" direction="column" justifyContent="center">
                <Divider style={{ width: '60%' }} />

                <DefaultButton
                  className={classes.button}
                  startIcon={<AddCircleOutlineRoundedIcon />}
                  onClick={onCreateAddressClick}
                >
                  Créer un lieu
                </DefaultButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography style={{ fontStyle: 'italic', fontWeight: 300, marginBottom: '16px', marginTop: '16px' }}>
          {message}
        </Typography>

        {!!dialog ? (
          <div className={classes.dialogActions}>
            <DefaultButton cancel onClick={onCancelFormClick}>
              Annuler
            </DefaultButton>
            <DefaultButton disabled={isSubmitting} standard type="submit">
              {isSubmitting ? <CircularProgress size={20} /> : 'Sauvegarder'}
            </DefaultButton>
          </div>
        ) : (
          <Grid container item xs={12} justifyContent="center">
            <Grid container item xs={12} alignItems="flex-end" justifyContent="center" style={{ marginBottom: '16px' }}>
              <DefaultButton cancel onClick={onCancelFormClick}>
                Annuler
              </DefaultButton>
              <DefaultButton color="secondary" disabled={isSubmitting} type="submit" variant="contained">
                {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
              </DefaultButton>
            </Grid>
          </Grid>
        )}
      </Grid>

      <ActionOnActivityModal
        loading={actionLoading}
        modalType={actionType.DEACTIVATE}
        onClose={handleCancelDeactivation}
        onConfirm={handleConfirmActionOnAddressModal}
        showAction={showAction}
      />

      <DefaultModal hideActions maxWidth="md" open={createModalsOpen.employee} title="Création d'un moniteur">
        <EmployeeForm
          id="employeeForm3"
          dialog
          employee={initialEmployeeState}
          onCancel={() => setCreateModalsOpen((prev) => ({ ...prev, employee: false }))}
          onSubmit={handleCreateEmployee}
        />
      </DefaultModal>

      <DefaultModal hideActions maxWidth="md" open={createModalsOpen.address} title="Création d'un lieu">
        <AddressForm
          id="addressForm2"
          address={initialAddressState}
          dialog
          onCancel={() => setCreateModalsOpen((prev) => ({ ...prev, address: false }))}
          onSubmit={handleCreateAddress}
        />
      </DefaultModal>
    </form>
  );
}

export default ActivityForm;
